import React, { useState, useEffect, useMemo } from 'react';
import { PauseCircle, Clock } from 'lucide-react';
import { useBrandConfig } from '../context/BrandConfigContext';

function getCountdownParts(resumeAt) {
  const target = new Date(resumeAt).getTime();
  if (isNaN(target)) return null;
  const diff = target - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
  const seconds = Math.floor((diff / 1000) % 60);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  return { days, hours, minutes, seconds, done: false };
}

function ordinalSuffix(day) {
  if (day % 10 === 1 && day !== 11) return 'st';
  if (day % 10 === 2 && day !== 12) return 'nd';
  if (day % 10 === 3 && day !== 13) return 'rd';
  return 'th';
}

// e.g. "20th Sept. 2026 at 11:00 AM"
function formatResumeDate(resumeAt) {
  const date = new Date(resumeAt);
  if (isNaN(date.getTime())) return '';
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'short' }).replace(/\.?$/, '.');
  const year = date.getFullYear();
  const time = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${day}${ordinalSuffix(day)} ${month} ${year} at ${time}`;
}

// All styling lives here so tweaks don't require touching the JSX below.
// The brand color is the only truly dynamic value, so it's passed in as the
// `--cpm-primary` CSS custom property on the overlay root.
const STYLES = `
  .cpm-overlay {
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background-color: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(6px);
  }
  .cpm-card {
    position: relative;
    width: 100%;
    max-width: 440px;
    padding: 48px 32px 32px;
    border-radius: 8px;
    text-align: center;
    background-color: var(--cpm-primary);
    box-shadow: 0px 24px 70px 0px rgba(0, 0, 0, 0.5);
  }
  .cpm-badge {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    padding: 10px;
    border-radius: 50%;
    background-color: var(--cpm-primary);
    box-shadow: 0px 6px 20px 0px rgba(0, 0, 0, 0.3), 0 0 0 6px rgba(255, 255, 255, 0.08);
  }
  .cpm-badge img {
    max-height: 52px;
    max-width: 62px;
    object-fit: contain;
  }
  .cpm-content {
    position: relative;
    z-index: 1;
  }
  .cpm-pill {
    display: inline-block;
    margin-bottom: 10px;
    padding: 4px 12px;
    border-radius: 50rem;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.14);
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.08em;
  }
  .cpm-heading {
    margin-bottom: 10px;
    color: #fff;
    font-weight: 700;
    font-size: clamp(1.25rem, 4vw, 1.5rem);
  }
  .cpm-message {
    margin: 0 auto 25px;
    max-width: 360px;
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.85rem;
    line-height: 1.5;
  }
  .cpm-countdown {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }
  .cpm-countdown-item {
    min-width: 58px;
    padding: 8px 10px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.18);
  }
  .cpm-countdown-value {
    color: #fff;
    font-weight: 700;
    font-size: 1.35rem;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
  }
  .cpm-countdown-label {
    color: rgba(255, 255, 255, 0.65);
    text-transform: uppercase;
    font-size: 0.6rem;
    letter-spacing: 0.06em;
  }
  .cpm-countdown-separator {
    color: rgba(255, 255, 255, 0.35);
    font-weight: 700;
    font-size: 1.2rem;
  }
  .cpm-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 0 auto;
    max-width: 360px;
    padding-top: 12px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
    border-top: 1px solid rgba(255, 255, 255, 0.14);
  }
`;

/**
 * Popup shown on the engagement portal's landing page while the platform
 * is PAUSED. Purely informational — the engine itself rejects/ignores all
 * requests while paused, this just tells the participant why nothing is
 * happening and (optionally) counts down to the resume time. Not dismissible:
 * entries can't be submitted while paused, so there's nothing to "get back to".
 */
const CampaignPausedModal = () => {
  const brand = useBrandConfig();
  const isPaused = brand?.platformStatus === 'PAUSED';
  const resumeAt = brand?.pauseResumeAt;
  const [countdown, setCountdown] = useState(() => getCountdownParts(resumeAt));

  useEffect(() => {
    if (!isPaused || !resumeAt) {
      setCountdown(null);
      return undefined;
    }
    // Compute immediately so the timer doesn't wait a full second to appear, then tick every second.
    setCountdown(getCountdownParts(resumeAt));
    const interval = setInterval(() => setCountdown(getCountdownParts(resumeAt)), 1000);
    return () => clearInterval(interval);
  }, [isPaused, resumeAt]);

  const message = useMemo(
    () => brand?.pauseMessage || "We're taking a short breather to make things even better. Hang tight — we'll be back before you know it!",
    [brand?.pauseMessage]
  );
  const pillText = brand?.pausePillText || 'Entries Temporarily Paused';
  const heading = brand?.pauseHeading || "We'll be right back";

  if (!isPaused) return null;

  // theme.primaryColor from brand config isn't reliably kept in sync with this
  // deployment's actual red skin (see custom.css), so fall back to that red
  // rather than the unrelated global --primary (which stays the base green).
  const primaryColor = brand?.theme?.primaryColor || '#de0b1c';

  const countdownParts = countdown && !countdown.done
    ? [
        { label: 'Days', value: countdown.days },
        { label: 'Hrs', value: countdown.hours },
        { label: 'Min', value: countdown.minutes },
        { label: 'Sec', value: countdown.seconds }
      ]
    : null;

  return (
    <div
      className="cpm-overlay"
      style={{ '--cpm-primary': primaryColor }}
      role="dialog"
      aria-modal="true"
      aria-label="Entries temporarily paused"
    >
      <style>{STYLES}</style>
      <div className="cpm-card">
        <div className="cpm-badge">
          {brand?.logoUrl ? (
            <img src={brand.logoUrl} alt={brand?.appName || 'Logo'} />
          ) : (
            <PauseCircle size={48} color={primaryColor} />
          )}
        </div>

        <div className="cpm-content">
          <span className="cpm-pill">{pillText}</span>
          <h2 className="cpm-heading">{heading}</h2>
          <p className="cpm-message">{message}</p>

          {countdownParts && (
            <div className="cpm-countdown">
              {countdownParts.map((part, i) => (
                <React.Fragment key={part.label}>
                  <div className="cpm-countdown-item">
                    <div className="cpm-countdown-value">{String(part.value).padStart(2, '0')}</div>
                    <div className="cpm-countdown-label">{part.label}</div>
                  </div>
                  {i < countdownParts.length - 1 && <span className="cpm-countdown-separator">:</span>}
                </React.Fragment>
              ))}
            </div>
          )}

          {resumeAt && (
            <div className="cpm-footer">
              <Clock size={14} />
              <span>Resuming {formatResumeDate(resumeAt)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignPausedModal;

